import { NextButton } from 'components/NextButton';
import { PrevButton } from 'components/PrevButton';
import { useContext, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormItem, WisoryInput, WisoryPhoneInput, WisoryTextArea } from 'components/Inputs';
import clsx from 'clsx';
import { ProfileFooter } from 'pages/profile/components/Layout/components/ProfileFooter';
import { BookingFormLayout } from 'pages/profile/components/Layout';
import { isMobile } from 'react-device-detect';
import { EMAIL_REGEX } from 'utils/regex';
import { BookingFormContext } from 'pages/profile/context2';
import { CustomerInfo } from 'pages/profile/types';
import { useQuery } from 'react-query';
import debouncePromise from 'awesome-debounce-promise';
import BaseAPI, { ValidationResultObject } from 'api';

type Props = {
  onSubmitSuccess: () => void;
  onPrevBtnCick: () => void;
};

export const GuestForm = ({ onSubmitSuccess, onPrevBtnCick }: Props) => {
  const { t } = useTranslation();
  const { state, setState } = useContext(BookingFormContext);
  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { errors },
  } = useForm<CustomerInfo>({ mode: 'onSubmit', defaultValues: state.customerInfo });
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const validatePhoneNumber = useQuery<ValidationResultObject>({
    queryFn: () => BaseAPI.api.validatePhoneNumber({ phoneNumber: getValues().customerPhoneNumber }),
    enabled: false,
  });

  const saveValueToStore = (fieldName: keyof CustomerInfo, value: any) => {
    setState((draft) => {
      // @ts-ignore
      draft.customerInfo[fieldName] = value;
    });
  };

  const sendContactEmail = () => {
    onSubmitSuccess();
  };

  return (
    <>
      <form onSubmit={handleSubmit(sendContactEmail)} className="text-white mb-0">
        <BookingFormLayout
          footer={
            <ProfileFooter className="flex justify-between">
              <PrevButton type="button" onClick={onPrevBtnCick} loading={validatePhoneNumber.isFetching}>
                {t('REACT_BOOKING_FRONTEND.common.backButton')}
              </PrevButton>
              <NextButton type="submit" componentRef={submitBtnRef} loading={validatePhoneNumber.isFetching}>
                {t('REACT_BOOKING_FRONTEND.common.nextButton')}
              </NextButton>
            </ProfileFooter>
          }
        >
          <div className={clsx('wisory-title', 'mb-2')}>{t('REACT_BOOKING_FRONTEND.booking2.guestForm.title')}</div>
          <div className="font-openSans mb-10 text-black">{t('REACT_BOOKING_FRONTEND.booking2.guestFormSubtitle')}</div>
          <div className="flex flex-col sm:flex-row">
            <div className="sm:w-1/2 sm:pr-4">
              <FormItem label={t('customer_name')} errorMessage={errors.customerName?.message}>
                <WisoryInput
                  {...register('customerName', {
                    required: t('REACT_BOOKING_FRONTEND.common.validations.required'),
                    onChange(e) {
                      saveValueToStore('customerName', e.target.value);
                    },
                  })}
                  autoFocus={!isMobile}
                  type="text"
                  placeholder={t('customer_name')}
                />
              </FormItem>
            </div>
            <div className="sm:w-1/2">
              <FormItem label={t('customer_company')} errorMessage={errors.customerCompany?.message}>
                <WisoryInput
                  {...register('customerCompany', {
                    required: t('REACT_BOOKING_FRONTEND.common.validations.required'),
                    onChange(e) {
                      saveValueToStore('customerCompany', e.target.value);
                    },
                  })}
                  type="text"
                  placeholder={t('customer_company')}
                />
              </FormItem>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row">
            <div className="sm:w-1/2 sm:pr-4">
              <FormItem label={t('customer_email')} errorMessage={errors.customerEmail?.message}>
                <WisoryInput
                  {...register('customerEmail', {
                    required: t('REACT_BOOKING_FRONTEND.common.validations.required'),
                    pattern: {
                      value: EMAIL_REGEX,
                      message: t('REACT_BOOKING_FRONTEND.common.validations.email'),
                    },
                    onChange(e) {
                      saveValueToStore('customerEmail', e.target.value);
                    },
                  })}
                  type="email"
                  placeholder={t('customer_email')}
                />
              </FormItem>
            </div>
            <div className="sm:w-1/2 ">
              <FormItem
                label={t('REACT_BOOKING_FRONTEND.customerPhoneNumber')}
                errorMessage={errors.customerPhoneNumber?.message}
              >
                <Controller
                  control={control}
                  name="customerPhoneNumber"
                  render={({ field }) => (
                    <WisoryPhoneInput
                      value={field.value}
                      onChange={(e?: string) => {
                        field.onChange(e);
                        saveValueToStore('customerPhoneNumber', e);
                      }}
                    />
                  )}
                  rules={{
                    required: t('REACT_BOOKING_FRONTEND.common.validations.required'),
                    validate: debouncePromise(async (value) => {
                      const response = await validatePhoneNumber.refetch();

                      return response.data?.data.isSuccess || t('PHONE_NUMBER_IS_INVALID');
                    }, 500),
                  }}
                />
              </FormItem>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row w-full">
            <FormItem label={t('customer_billing_address')} errorMessage={errors.customerBillingAddress?.message}>
              <WisoryTextArea
                style={{ height: 176 }}
                {...register('customerBillingAddress', {
                  required: t('REACT_BOOKING_FRONTEND.common.validations.required'),
                  onChange(e) {
                    saveValueToStore('customerBillingAddress', e.target.value);
                  },
                })}
              />
            </FormItem>
          </div>
        </BookingFormLayout>
      </form>
    </>
  );
};
